/*
 * PURCHASE INCREMENTS — the part the +/- buttons cannot cover.
 *
 * min and step on the input already make the BUTTONS step 2, 4, 6 and never 3: the browser steps
 * from min in step units. But a number input is still a text field. Typing 3 into it, or arriving
 * with a stale value, produces a quantity no button could have produced — and Dawn submits the
 * product form through fetch, so the browser's own step validation never runs.
 *
 * Two guards, in the order a person meets them:
 *   1. leaving the field snaps the value DOWN to the nearest whole number of increments, because
 *      rounding up would silently add a chair to somebody's basket
 *   2. submitting revalidates, including whatever is already in the cart for that variant, and
 *      refuses with the shop's own wording rather than a browser default
 *
 * Neither is the real enforcement. A cart/checkout validation function is (see HANDOFF), because
 * anything in this file can be bypassed by anyone willing to open a console.
 */
(function () {
  const incOf = (input) => {
    const n = parseInt(input.getAttribute('data-quantity-increment'), 10);
    return Number.isFinite(n) && n > 1 ? n : 1;
  };

  /* The largest valid quantity at or below `value`. Never rounds up: the customer asked for at most
     this many, and handing them more is the one direction that costs money. */
  function snapDown(value, inc, min, max) {
    let v = Math.floor(value / inc) * inc;
    if (Number.isFinite(max)) v = Math.min(v, Math.floor(max / inc) * inc);
    if (v < min) v = min;
    return v;
  }

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!input || !input.matches || !input.matches('input[data-quantity-increment]')) return;
      const inc = incOf(input);
      if (inc === 1) return;

      const min = parseInt(input.min, 10) || 0;
      const max = parseInt(input.max, 10);
      const raw = parseInt(input.value, 10);
      if (!Number.isFinite(raw)) return;

      // 0 is a real answer in the cart: it means remove this line.
      if (raw <= 0 && min === 0) return;

      const snapped = snapDown(raw, inc, Math.max(min, inc), Number.isFinite(max) ? max : Infinity);
      if (snapped !== raw) {
        input.value = snapped;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },
    true
  );

  /* The REFUSAL always names the number — "sold in multiples of 2" — even where the shop's own
     browsing copy prefers "sold in pairs". A customer who has just been stopped needs the rule
     stated arithmetically, because the next thing they do is work out what to type. */
  function messageFor(input, inc) {
    const tpl = input.getAttribute('data-increment-error');
    if (!tpl) return null;
    return tpl.replace('{{ count }}', String(inc)).replace('{{count}}', String(inc));
  }

  /* Dawn's error slot is NOT inside the <form> — it sits in the surrounding <product-form> element,
     which is why searching the form alone found nothing and fell through to a browser dialog. Widen
     the search outward: the product-form wrapper first, the document as a last resort. */
  function showError(form, text) {
    const scope = form.closest('product-form') || form.parentElement || document;
    const wrapper = scope.querySelector('.product-form__error-message-wrapper')
      || document.querySelector('.product-form__error-message-wrapper');
    if (!wrapper) return false;
    const slot = wrapper.querySelector('.product-form__error-message') || wrapper;
    slot.textContent = text;
    wrapper.setAttribute('role', 'alert');
    wrapper.hidden = false;
    return true;
  }

  /* DAWN RESETS THE VALUE TO min, AND IT DOES IT AFTER WE RENDER.
     product-info.js's setQuantityBoundries() ends with `this.quantityInput.value = min`, so the
     server's "start at 6" was being pulled straight back down to 2 on load and again on every
     variant change. It sets .min/.max as it goes, which reflect to attributes, so watching those is
     a reliable signal that it has just run — no reaching into Dawn's own pub/sub.

     Only until the customer touches the control. After that the number on screen is THEIR answer and
     nothing here may move it. */
  const startAtMax = (input) => {
    if (input.dataset.qtyUserTouched === '1') return;
    const inc = incOf(input);
    if (inc === 1) return;
    const max = parseInt(input.max, 10);
    if (!Number.isFinite(max) || max < inc) return;
    const want = Math.floor(max / inc) * inc;
    if (String(want) !== input.value) {
      input.value = want;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  function armStartAtMax(input) {
    if (input.dataset.qtyStartArmed === '1') return;
    input.dataset.qtyStartArmed = '1';
    const mark = () => { input.dataset.qtyUserTouched = '1'; };
    // A press on either stepper button, or typing, is the customer taking over.
    const holder = input.closest('quantity-input');
    if (holder) holder.querySelectorAll('button').forEach((b) => b.addEventListener('click', mark));
    input.addEventListener('input', mark);
    new MutationObserver(() => startAtMax(input)).observe(input, {
      attributes: true,
      attributeFilter: ['min', 'max'],
    });
    startAtMax(input);
  }

  const armAll = () =>
    document
      .querySelectorAll('.product-form__quantity input[data-quantity-increment]')
      .forEach(armStartAtMax);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', armAll);
  } else {
    armAll();
  }
  // The product section re-renders on a variant change, replacing the input.
  document.addEventListener('change', (e) => {
    if (e.target && e.target.name === 'id') setTimeout(armAll, 300);
  });

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
      /* The quantity input is OUTSIDE the form and associated by its form= attribute, so the
         fallback lookup is the real path here — and it has to read the id with getAttribute.
         `form.id` does NOT return the id string: a form exposes its named controls as properties,
         and this form contains a hidden input named "id" (the variant), which shadows it. Reading
         form.id therefore yielded an element, the selector became [form="[object HTMLInputElement]"],
         nothing matched, and this guard returned early having validated nothing — it let a hand-set
         quantity of 3 straight through. */
      const formId = form.getAttribute('id');
      const input = form.querySelector('input[data-quantity-increment]')
        || (formId ? document.querySelector('input[data-quantity-increment][form="' + formId + '"]') : null);
      if (!input) return;
      const inc = incOf(input);
      if (inc === 1) return;

      const asked = parseInt(input.value, 10) || 0;
      /* THE WHOLE LINE, not this press. Two already in the basket plus two more is four, which is
         valid; the rule is about what the order ends up holding, so the check has to add them.

         data-product-in-cart counts EVERY variant of this product, not just the selected one. On a
         product whose upholsteries share one physical pool, two chairs in one fabric and two in
         another are four out of the same six — counting the selected line alone is what would let
         somebody take six of each. */
      const already = parseInt(input.getAttribute('data-product-in-cart'), 10)
        || parseInt(input.getAttribute('data-cart-quantity'), 10) || 0;
      const total = asked + already;

      /* The pool caps the total too, regardless of increments: no press may take the order past
         what physically exists. */
      const pool = parseInt(input.getAttribute('data-shared-pool'), 10);
      if (Number.isFinite(pool) && total > pool) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const left = Math.max(0, pool - already);
        input.value = Math.floor(left / inc) * inc;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      if (asked > 0 && total % inc === 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      /* Refusing in silence would read as a dead button, so if there is genuinely nowhere to put the
         message the quantity is snapped to something valid instead and the customer can press again.
         No dialog: a modal here would block the page and tell them nothing they can act on. */
      const text = messageFor(input, inc);
      if (!text || !showError(form, text)) {
        const min = parseInt(input.min, 10) || inc;
        const max = parseInt(input.max, 10);
        input.value = snapDown(Math.max(asked, inc), inc, Math.max(min, inc), Number.isFinite(max) ? max : Infinity);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },
    true
  );
})();
